<template>
  <el-select
    :value="value"
    :multiple="multiple"
    filterable
    remote
    clearable
    reserve-keyword
    :remote-method="onSearch"
    :loading="loading"
    :popper-append-to-body="false"
    v-bind="$attrs"
    v-on="$listeners"
    @visible-change="onVisibleChange"
    @change="onChange"
  >
    <el-option v-for="item in options" :key="item[valueKey]" :label="itemLabel(item)" :value="item[valueKey]" />
    <el-option v-if="loading" key="__psLoading__" value="__psLoading__" label="" disabled>
      <span class="page-select-tip">加载中...</span>
    </el-option>
    <el-option v-else-if="hasMore" key="__psMore__" value="__psMore__" label="" disabled>
      <span class="page-select-tip">下拉到底自动加载更多（共 {{ total }} 条）</span>
    </el-option>
    <el-option v-else-if="options.length > 0" key="__psEnd__" value="__psEnd__" label="" disabled>
      <span class="page-select-tip">已全部加载（共 {{ total }} 条）</span>
    </el-option>
  </el-select>
</template>
<script>
// 远程搜索 + 分页加载的下拉框：
// 打开时载第一页，输入关键字后端模糊搜（SearchKey），下拉滚到底自动加载下一页；
// 编辑回显时按 Id 补拉单条；多选时已选项不因翻页丢 label。
// 用法：<page-select v-model="form.CustomerId" :load-api="tradeUnitApi.list"
//         value-key="TradeUnitId" label-key="ShortName" :fixed-params="{ TradeUnitTypes: [0] }" />
export default {
  name: 'PageSelect',
  props: {
    value: { type: [String, Number, Array], default: '' },
    // 加载函数：传查询参数对象，返回 { Code, Data, Count }
    loadApi: { type: Function, required: true },
    // 选项的取值字段名，如 TradeUnitId / ProductId / EquipmentId
    valueKey: { type: String, required: true },
    // 选项的显示字段名；显示格式复杂的用 labelFn
    labelKey: { type: String, default: '' },
    labelFn: { type: Function, default: null },
    // 固定过滤参数，如 { TradeUnitTypes: [0], Status: 1 }
    fixedParams: { type: Object, default: () => ({}) },
    // 搜索词传给后端的字段名
    searchField: { type: String, default: 'SearchKey' },
    pageSize: { type: Number, default: 50 },
    multiple: { type: Boolean, default: false }
  },
  data() {
    return {
      options: [],
      total: 0,
      pageIndex: 0,
      query: '',
      loading: false
    };
  },
  computed: {
    hasMore() {
      return this.options.length < this.total;
    }
  },
  watch: {
    value: {
      immediate: true,
      handler() {
        this.ensureSelected();
      }
    }
  },
  beforeDestroy() {
    this.unbindScroll();
  },
  methods: {
    itemLabel(item) {
      if (this.labelFn) return this.labelFn(item);
      return item[this.labelKey || this.valueKey];
    },
    // 把一条记录塞进选项（去重），外部新增保存后也可调用回填
    appendOption(item) {
      if (!item) return;
      if (!this.options.some(o => o[this.valueKey] === item[this.valueKey])) {
        this.options.push(item);
      }
    },
    buildParams(page) {
      var params = Object.assign({}, this.fixedParams);
      if (this.query) params[this.searchField] = this.query;
      params.PageIndex = page;
      params.PageSize = this.pageSize;
      return params;
    },
    load(page) {
      if (this.loading) {
        // 加载中又发起的搜索先记下，当前加载完成后再跑，避免丢输入
        this._pendingPage = page;
        return;
      }
      this.loading = true;
      var self = this;
      this.loadApi(this.buildParams(page)).then(res => {
        if (res.Code == 0) {
          self.total = res.Count || 0;
          // 第 1 页整体替换（loading 期间旧列表保留，不闪屏）；翻页追加。
          // 替换时保留"当前已选中但新列表里没有"的项，否则多选已选项显示成 id、单选回显被清空
          if (page === 1) {
            var cur = this.multiple ? (Array.isArray(this.value) ? this.value : []) : (this.value !== null && this.value !== undefined && this.value !== '' ? [this.value] : []);
            var newData = res.Data || [];
            var kept = self.options.filter(o => cur.indexOf(o[self.valueKey]) >= 0 && !newData.some(n => n[self.valueKey] === o[self.valueKey]));
            self.options = kept;
          }
          (res.Data || []).forEach(o => self.appendOption(o));
          self.pageIndex = page;
        }
        self.loading = false;
        if (self._pendingPage) {
          var p = self._pendingPage;
          self._pendingPage = 0;
          self.load(p);
        }
      }).catch(() => {
        self.loading = false;
        self._pendingPage = 0;
      });
    },
    // 输入关键字搜索（防抖，避免每个字符都发请求）
    onSearch(query) {
      clearTimeout(this._searchTimer);
      var self = this;
      this._searchTimer = setTimeout(() => {
        self.query = (query || '').trim();
        // 仅"空输入且之前也是空过滤且已加载过"时跳过（打开下拉的重复触发）；
        // 清空关键字要重新载第一页全量，不能跳
        if (!self.query && !self._lastQuery && self.pageIndex > 0) return;
        self._lastQuery = self.query;
        self.load(1);
      }, 400);
    },
    onVisibleChange(visible) {
      if (visible) {
        if (this.options.length === 0 && this.pageIndex === 0) this.load(1);
        this.$nextTick(this.bindScroll);
      } else {
        this.unbindScroll();
      }
    },
    // 下拉滚到底自动加载下一页
    bindScroll() {
      var wrap = this.$el.querySelector('.el-scrollbar__wrap');
      if (!wrap || this._scrollWrap === wrap) return;
      var self = this;
      this._scrollHandler = function () {
        if (self.loading || !self.hasMore) return;
        if (wrap.scrollTop + wrap.clientHeight >= wrap.scrollHeight - 5) {
          self.load(self.pageIndex + 1);
        }
      };
      wrap.addEventListener('scroll', this._scrollHandler);
      this._scrollWrap = wrap;
    },
    unbindScroll() {
      if (this._scrollWrap && this._scrollHandler) {
        this._scrollWrap.removeEventListener('scroll', this._scrollHandler);
      }
      this._scrollWrap = null;
      this._scrollHandler = null;
    },
    onChange(val) {
      // 先更新 v-model 再发 row-change：父级联动逻辑要按新值判断
      this.$emit('input', val);
      if (this.multiple) {
        var vals = Array.isArray(val) ? val : [];
        var rows = this.options.filter(o => vals.indexOf(o[this.valueKey]) >= 0);
        this.$emit('row-change', rows);
      } else {
        var row = val === '' || val === null ? null : this.options.find(o => o[this.valueKey] === val) || null;
        this.$emit('row-change', row);
      }
    },
    // 回显：已选值不在选项里时按 Id 补拉（依赖后端 GetQueryByParam 的 Id 精确过滤）。
    // 补拉剔除 Status 过滤：已停用的记录编辑时也要能回显名称
    ensureSelected() {
      var val = this.value;
      var empty = val === null || val === undefined || val === '' || (Array.isArray(val) && val.length === 0);
      if (empty) return;
      var vals = Array.isArray(val) ? val : [val];
      var self = this;
      vals.forEach(id => {
        if (self.options.some(o => o[self.valueKey] === id)) return;
        var params = Object.assign({}, self.fixedParams, { Id: id, PageIndex: 1, PageSize: self.pageSize });
        delete params.Status;
        self.loadApi(params).then(res => {
          if (res.Code == 0 && res.Data && res.Data.length > 0) {
            self.appendOption(res.Data[0]);
          }
        });
      });
    },
    // 清空缓存和已加载页，下次打开下拉时会重新拉第一页（用于对话框打开时刷新选项）
    reset() {
      this.options = [];
      this.total = 0;
      this.pageIndex = 0;
      this.query = '';
      this._lastQuery = '';
      clearTimeout(this._searchTimer);
    }
  }
};
</script>
<style>
.page-select-tip {
  display: block;
  text-align: center;
  color: #96A39B;
  font-size: 12px;
  line-height: 24px;
  pointer-events: none;
}
</style>
